"use client"

import { ReactNode, useEffect, useLayoutEffect, useRef } from "react"

interface FixedBlockProps {
  children?: ReactNode
}

// Safe-area cushion applied to the wrapper ONLY when it has content. Lives in
// padding (not in --fixed-block-height, which stays content-only) and is never
// touched by the legacy RN tab-bar bridge, so it composes with the bridge's
// imperative `bottom` clobber on older shells.
const SAFE_AREA_PADDING =
  "var(--tc-safe-bottom-eff, env(safe-area-inset-bottom))"
// Pre-PR cushion: bare env(), matching the old `pb-safe` class. Used on every
// non-glass shell so this adapter is inert unless Liquid Glass is present.
const LEGACY_SAFE_AREA_PADDING = "env(safe-area-inset-bottom)"

export const getSafeAreaPadding = (rootClassName: string): string => {
  if (rootClassName.includes("tc-platform-android")) return "0px"
  // Only glass shells adopt the frame-relative cushion; everything else keeps
  // the pre-PR env() cushion so legacy / glass-off layout is unchanged.
  return rootClassName.includes("tc-glass")
    ? SAFE_AREA_PADDING
    : LEGACY_SAFE_AREA_PADDING
}

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect

const FixedBlock = ({ children }: FixedBlockProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const outerRef = useRef<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    const updateHeight = () => {
      const inner = containerRef.current
      const outer = outerRef.current
      if (!inner || !outer) {
        return
      }

      // FixedBlock is mounted unconditionally, but most screens portal no
      // content into it. When empty, collapse the wrapper to zero so it does not
      // paint a permanent opaque strip over — and intercept taps at — the bottom
      // of footer-less screens (regression previously fixed in 078bc91c6). We
      // gate on childElementCount rather than CSS :empty/:has() because the
      // wrapper always has one child (the container) and :has() needs iOS 15.4
      // (deploy target is 15.1).
      const isEmpty = inner.childElementCount === 0
      outer.style.paddingBottom = isEmpty
        ? "0px"
        : getSafeAreaPadding(document.documentElement.className)

      // Under glass, #tc-vgsl owns the frame, so <main> only needs the block's
      // content height. On non-glass shells the pre-PR contract holds: <main>
      // clears the full block box (content + env cushion), so publish the outer
      // height and keep legacy/glass-off layout byte-for-byte unchanged.
      const glass = document.documentElement.classList.contains("tc-glass")
      const publishedHeight = isEmpty
        ? 0
        : glass
        ? inner.offsetHeight
        : outer.offsetHeight
      document.documentElement.style.setProperty(
        "--fixed-block-height",
        `${publishedHeight}px`
      )
    }

    // Update height initially
    updateHeight()

    const container = containerRef.current

    // Update height when the container resizes or children are added/removed via
    // portals.
    const resizeObserver = new ResizeObserver(updateHeight)
    const mutationObserver = new MutationObserver(updateHeight)
    if (container) {
      resizeObserver.observe(container)
      mutationObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      })
    }

    // updateHeight branches on the root's .tc-glass class (cushion source +
    // whether --fixed-block-height is content-only or the full box). The shell
    // may add .tc-glass AFTER content has already portaled in, so re-run when
    // the root class changes — otherwise the published height keeps the stale
    // non-glass value and the safe area gets double-counted once glass turns on.
    const rootObserver = new MutationObserver(updateHeight)
    rootObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })

    // Update height on window resize as well
    window.addEventListener("resize", updateHeight)

    // Clean up
    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      rootObserver.disconnect()
      window.removeEventListener("resize", updateHeight)
      document.documentElement.style.removeProperty("--fixed-block-height")
    }
  }, []) // No dependency on children since we're using MutationObserver

  return (
    <div
      ref={outerRef}
      data-tc-fixed-block
      className="fixed z-50 w-full bg-coreColors-pageColor"
      style={{
        // Additive decomposition of bottom clearance. `bottom` clears the bar
        // footprint ABOVE the safe area; the safe-area cushion lives in
        // paddingBottom (set imperatively in updateHeight). On legacy shells the
        // RN bridge (useTabBarPaddingInjection) imperatively overwrites `bottom`
        // to a safe-exclusive value on iOS. Android keeps the pre-PR, native-
        // framed behavior: --tc-frame-bottom and the web safe cushion are both
        // zero; any legacy bridge writes remain unchanged.
        bottom:
          "max(calc(var(--tc-frame-bottom, 0px) - var(--tc-safe-bottom-eff, 0px)), 0px)",
        // SSR/first-paint default: collapsed. updateHeight expands to the safe
        // cushion once content is portaled in, and re-collapses when it empties.
        paddingBottom: "0px",
      }}
    >
      {/* Dedicated container for React portals */}
      <div
        id="layout-fixed-block-container"
        className="flex flex-col"
        ref={containerRef}
      >
        {children}
      </div>
    </div>
  )
}

export default FixedBlock
